/**
 * One site's audit history, every audit, newest first.
 *
 * Deliberately a list of runs rather than a second rendering of the findings.
 * The findings already have a page that is built to be read by somebody outside
 * this company, and a private copy of it here would be a second thing to keep
 * true. What this dialog adds is the part the report cannot know: how often we
 * looked, which channel brought them, whether they gave an address, and whether
 * they ever opened what we sent.
 */
import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Modal, fmtDateTime } from "../../ui";
import { auditBand, siteAuditHistory, type AuditHistoryItem, type AuditKind } from "../../../lib/siteAudits";
import { AUDIT_KIND_META } from "./auditKinds";

const BAND_COLOUR = {
  good: "var(--color-success, #16a34a)",
  mid: "var(--color-warning, #d97706)",
  bad: "var(--color-destructive, #dc2626)",
} as const;

export interface AuditHistoryLabels {
  title: string;
  /** One label per audit, keyed by kind. See `auditKinds.ts`. */
  kinds: Record<AuditKind, string>;
  openReport: string;
  opened: string;
  campaign: string;
  gaveAddress: string;
  noAddress: string;
  loading: string;
  failed: string;
}

export function AuditHistoryDialog({
  domain,
  labels,
  onClose,
}: {
  domain: string;
  labels: AuditHistoryLabels;
  onClose: () => void;
}) {
  const [items, setItems] = useState<AuditHistoryItem[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    siteAuditHistory(domain, ctrl.signal)
      .then((res) => {
        if (!ctrl.signal.aborted) setItems(res.audits);
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setFailed(true);
      });
    return () => ctrl.abort();
  }, [domain]);

  return (
    <Modal title={`${labels.title}: ${domain}`} onClose={onClose}>
      {failed ? (
        <p className="crm-mut py-8 text-center text-[13px]">{labels.failed}</p>
      ) : items == null ? (
        <p className="crm-mut py-8 text-center text-[13px]">{labels.loading}</p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {items.map((a) => {
            const colour = BAND_COLOUR[auditBand(a.score)];
            // A row was rendered through `a.kind === "seo" ? ... : ...`, so an
            // unrecognised kind silently drew the agent audit's icon and name.
            // Looked up now, with a visible fallback: a row we cannot name says
            // so rather than pretending to be a different audit.
            const meta = AUDIT_KIND_META[a.kind as AuditKind] as typeof AUDIT_KIND_META[AuditKind] | undefined;
            const Icon = meta?.icon ?? ExternalLink;
            const kindLabel = labels.kinds[a.kind as AuditKind] ?? a.kind;
            return (
              <li
                key={a.id}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-[var(--color-border)] p-3"
              >
                <span
                  className="crm-tnum inline-flex h-9 min-w-[42px] items-center justify-center rounded-lg px-1.5 text-[14px] font-bold"
                  style={{ background: `color-mix(in oklab, ${colour} 14%, transparent)`, color: colour }}
                >
                  {a.score}
                </span>
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold">
                    <Icon className="size-[14px]" strokeWidth={1.8} />
                    {kindLabel}
                  </span>
                  <span className="crm-mut crm-tnum text-[11.5px]">{fmtDateTime(a.createdAt)}</span>
                </span>

                <span className="flex min-w-0 flex-col leading-tight text-[12.5px]">
                  <span className="crm-trunc">
                    {a.email ? `${labels.gaveAddress}: ${a.email}` : labels.noAddress}
                  </span>
                  <span className="crm-mut crm-trunc text-[11.5px]">
                    {a.sourceCampaign ? `${labels.campaign}: ${a.sourceCampaign}` : null}
                    {a.openCount > 0 ? ` ${labels.opened}: ${a.openCount}` : null}
                  </span>
                </span>

                <a
                  href={a.reportUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="crm-push inline-flex items-center gap-1.5 text-[12.5px] text-[var(--color-primary)] hover:underline"
                >
                  <ExternalLink className="size-[14px]" strokeWidth={1.8} />
                  {labels.openReport}
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
